import { useEffect, useRef, useState } from "react";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { API_BASE, getToken, isNative } from "./api";
import { checkSession, finishGoogleSignIn, GOOGLE_ERRORS, googleStartUrl, lastSignInOptions, loadNativeToken, login, recordSignOut, signInOptions, wasSignedIn } from "./auth";
import { db, onCorrupt, useSyncState } from "./db/client";
import { Logo } from "./ui/Logo";
import { startNativeBridge } from "./nativeBridge";
import { Shell } from "./ui/Shell";
import { prefetchAttachments } from "./ui/attachments";
import { startMove } from "./moved";
import { markAppReady, startUpdateChecks } from "./updater";
import { booting, lift, liftWhenReady } from "./boot";

type Phase = "starting" | "locked" | "taken" | "login" | "app" | "failed";

const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("engram") : null;
let releaseLock: (() => void) | null = null;
// Set in a tab that handed the database to another tab, so its reload doesn't grab it back.
const YIELDED = "engram.yielded";
const yielded = {
  get: () => {
    try {
      return sessionStorage.getItem(YIELDED);
    } catch {
      return null;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) sessionStorage.setItem(YIELDED, "1");
      else sessionStorage.removeItem(YIELDED);
    } catch {
      /* private mode */
    }
  },
};

/**
 * The OPFS database can only be open in one tab at a time. The tab holding the
 * Web Lock owns it; another tab can ask it to hand over.
 */
let lockAttempt: Promise<boolean> | null = null;
function acquireLock(wait: boolean): Promise<boolean> {
  // Shared so StrictMode's double effect doesn't make the tab compete with itself.
  return (lockAttempt ??= requestLock(wait).then((ok) => {
    if (!ok) lockAttempt = null;
    return ok;
  }));
}

/** `wait`: queue for the lock (taking over from another tab) instead of giving up when it's held. */
function requestLock(wait: boolean): Promise<boolean> {
  if (!navigator.locks) return Promise.resolve(true);
  return new Promise((resolve) => {
    navigator.locks
      .request("engram-db", wait ? { signal: AbortSignal.timeout(10_000) } : { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return;
        }
        resolve(true);
        return new Promise<void>((release) => (releaseLock = release));
      })
      .catch(() => resolve(false));
  });
}

export function App() {
  const [phase, setPhase] = useState<Phase>("starting");
  const [error, setError] = useState("");
  // The local database opening (sqlite-wasm + OPFS); the sign-in screen doesn't wait for it, the app does.
  const dbReady = useRef<Promise<boolean>>(Promise.resolve(false));

  async function start(takeover = false) {
    if ((!takeover && yielded.get()) || !(await acquireLock(takeover))) {
      setPhase("locked");
      return;
    }
    yielded.set(false);
    try {
      await loadNativeToken();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("failed");
      return;
    }
    const ready = db.init({ apiBase: API_BASE, token: getToken() }).then(
      () => (performance.mark("engram:db-ready"), true),
      (e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        setPhase("failed");
        return false;
      },
    );
    dbReady.current = ready;
    // Local-first: a device that was signed in opens straight from its replica; the server check runs alongside
    // and signs out only if the server rejects this device.
    if (wasSignedIn()) {
      if (!(await ready)) return;
      setPhase("app");
      void checkSession().then((s) => {
        if (s === "no") setPhase("login");
        else if (s === "ok") void db.sync();
      });
      return;
    }
    // Never signed in here (or signed out): ask the server while the database opens, and show the sign-in screen as
    // soon as it says no (a first visit saw it ~1.8 s sooner, chat 10).
    const session = await checkSession();
    if (session === "no" || session === "offline") {
      if (session === "offline") recordSignOut(0, "offline-first-run");
      setPhase((p) => (p === "failed" ? p : "login"));
      return;
    }
    if (!(await ready)) return;
    setPhase("app");
    void db.sync();
  }

  // A damaged local database (found by any query) stops the app and offers a rebuild from the server.
  useEffect(
    () =>
      onCorrupt((e) => {
        setError(e);
        setPhase("failed");
      }),
    [],
  );

  // Android: the app is up, so a new version's first start counts as working (see updater.ts).
  useEffect(() => {
    // The start screen lifts once this screen is complete (boot.ts); the other screens are complete as drawn.
    if (phase === "app" || phase === "login") liftWhenReady();
    else if (phase !== "starting") lift();
    if (phase === "app" || phase === "login") markAppReady();
    if (phase === "app") startUpdateChecks();
    // On the old workers.dev address: move to engrams.pages.dev once nothing here waits to sync (moved.ts).
    if (phase === "app" || phase === "login") startMove(phase);
  }, [phase]);

  useEffect(() => {
    void start();
    if (!channel) return;
    channel.onmessage = async (ev) => {
      if (ev.data === "takeover" && releaseLock) {
        // Another tab wants the database: finish pending saves (typing from a moment ago), close it (freeing the
        // OPFS handles) before letting go of the lock, then reload into the "open elsewhere" state without trying
        // to take it back. The other tab waits up to 10 s for the lock.
        const release = releaseLock;
        releaseLock = null;
        await db.saveAll();
        setPhase("taken");
        db.close();
        yielded.set(true);
        release();
        lockAttempt = null;
        setTimeout(() => location.reload(), 50);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "starting") return <div className="splash"><Logo size={28} /></div>;
  if (phase === "failed") return <Damaged error={error} />;
  if (phase === "locked" || phase === "taken")
    return (
      <div className="splash">
        <Logo size={28} />
        <p className="muted">Engram is open in another tab.</p>
        <button
          className="btn primary"
          onClick={() => {
            yielded.set(false);
            channel?.postMessage("takeover");
            setPhase("starting");
            void start(true);
          }}
        >
          Use here
        </button>
      </div>
    );
  if (phase === "login")
    return (
      <Login
        onDone={async () => {
          if (!(await dbReady.current)) return;
          setPhase("app");
          await db.sync();
        }}
      />
    );
  return (
    <>
      <SyncLoop />
      <Shell onSignedOut={() => setPhase("login")} />
    </>
  );
}

/**
 * The copy of the notes on this device can't be opened. The server has everything that was synced, so the fix is
 * to throw the local copy away and download it again.
 */
function Damaged({ error }: { error: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  return (
    <div className="splash">
      <div className="login damaged">
        <Logo size={28} />
        <h1>This device's copy of your notes is damaged</h1>
        <p className="muted small">
          Your notes are safe on the server. Rebuilding downloads them to this device again. Changes made here that hadn't synced yet can't be recovered.
        </p>
        <button
          className="btn primary wide"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await db.rebuild({ apiBase: API_BASE, token: getToken() });
              location.reload();
            } catch (e) {
              setFailed(e instanceof Error ? e.message : String(e));
              setBusy(false);
            }
          }}
        >
          {busy ? "Rebuilding…" : "Rebuild from server"}
        </button>
        <details className="small muted">
          <summary>Details</summary>
          <code>{failed || error}</code>
        </details>
      </div>
    </div>
  );
}

/** Poll on focus and every ~60 s while visible (CLAUDE.md sync policy). */
function SyncLoop() {
  const sync = useSyncState();
  useEffect(() => startNativeBridge(), []);
  useEffect(() => {
    // Coming back to the app fires focus and visibilitychange within a few ms: one pull, not two. (Kept short: a
    // 500 ms window also swallowed a real second return to the app, which then waited for the next poll.)
    let last = 0;
    const tick = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 100) return;
      last = Date.now();
      void db.sync();
    };
    const iv = setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  useEffect(() => {
    if (sync.status === "unauthorized") location.reload();
  }, [sync.status]);
  // After each completed sync, fill the offline attachment cache in the background.
  useEffect(() => {
    if (sync.status !== "idle" || !sync.lastSync) return;
    const t = setTimeout(() => void prefetchAttachments(), 3000);
    return () => clearTimeout(t);
  }, [sync.status, sync.lastSync]);
  return null;
}

/** Error from a Google sign-in that came back to the web app (`/?signin_error=…`), read once. */
function takeSigninError(): string | null {
  const q = new URLSearchParams(location.search);
  const key = q.get("signin_error");
  if (!key) return null;
  q.delete("signin_error");
  history.replaceState(null, "", location.pathname + (q.size ? `?${q}` : "") + location.hash);
  return GOOGLE_ERRORS[key] ?? GOOGLE_ERRORS.failed!;
}

/**
 * Sign-in: "Continue with Google" leads (owner, 12 Oct: Google replaces the passkey), the password below it as the
 * fallback. Same screen in the Android app, where Google runs in a Custom Tab.
 */
function Login({ onDone }: { onDone: () => void }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(takeSigninError);
  const [busy, setBusy] = useState(false);
  const [{ google }, setOptions] = useState(lastSignInOptions);
  useEffect(() => void booting(signInOptions()).then(setOptions), []);
  const googleBtn = useRef<HTMLButtonElement>(null);
  const pwInput = useRef<HTMLInputElement>(null);
  // Enter uses Google (and a phone doesn't open its keyboard), unless typing has started.
  useEffect(() => {
    if (!pw) (google ? googleBtn : pwInput).current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google]);
  // Android: Google sign-in runs in a Custom Tab and comes back through engram://auth?code=… (or ?error=…).
  useEffect(() => {
    if (!isNative) return;
    const handle = async (url: string) => {
      const u = new URL(url);
      if (u.host !== "auth") return;
      void Browser.close().catch(() => undefined);
      const code = u.searchParams.get("code");
      if (!code) {
        setErr(GOOGLE_ERRORS[u.searchParams.get("error") ?? ""] ?? GOOGLE_ERRORS.failed!);
        return;
      }
      setBusy(true);
      const error = await finishGoogleSignIn(code);
      setBusy(false);
      if (error) setErr(error);
      else onDone();
    };
    const sub = CapApp.addListener("appUrlOpen", ({ url }) => void handle(url));
    void CapApp.getLaunchUrl().then((r) => {
      if (r?.url) void handle(r.url);
    });
    return () => void sub.then((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="splash">
      <form
        className="login"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          const error = await login(pw);
          setBusy(false);
          if (error) setErr(error);
          else onDone();
        }}
      >
        <Logo size={34} />
        {google && (
          <button
            ref={googleBtn}
            type="button"
            className="btn wide google-btn"
            disabled={busy}
            onClick={() => {
              setErr(null);
              if (isNative) void Browser.open({ url: googleStartUrl() });
              else location.href = googleStartUrl();
            }}
          >
            <GoogleG /> Continue with Google
          </button>
        )}
        {google && <div className="login-or">or use your password</div>}
        <input ref={pwInput} className="input" type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" />
        {err && <div className="error-text">{err}</div>}
        <button className={google ? "btn wide" : "btn primary wide"} disabled={busy || !pw}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/** Google's "G" mark, as its sign-in branding asks for. */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
